import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import urljoin from 'url-join';
import { RoleType, UserInfo } from '../models';
import { Logger, LogService } from '../util';

export interface DataResponse<T> {
  data: T;
  meta: Record<string, unknown>;
}

@Injectable({
  providedIn: 'root',
})
export class SharedSessionService {
  private readonly _logger: Logger;

  // For admin only!
  private isChangePassRequired = false;

  userInfo?: UserInfo;

  constructor(private readonly http: HttpClient, private readonly logService: LogService) {
    this._logger = this.logService.createLogger(SharedSessionService.name);
  }

  async initializeService(): Promise<void> {
    try {
      const sessionData = await Promise.resolve(
        lastValueFrom(
          this.http.get<DataResponse<UserInfo>>(
            urljoin(environment.backendServiceUrl, '/v1/account/me'),
          ),
        ),
      );

      if (!environment.production) {
        this._logger.info(`Current user: `, sessionData?.data);
      }
      this.userInfo = sessionData?.data;
    } catch (ex) {
      this._logger.error(`Cannot fetch user info: `, ex);
    }
  }

  get isLoggedIn(): boolean {
    return !!this.userInfo;
  }

  get userRole(): RoleType | undefined {
    return this.userInfo?.role ?? undefined;
  }

  get cartTotal(): number | undefined {
    return this.userInfo?.cart?.length ?? undefined;
  }

  get userEmail(): string {
    return this.userInfo?.email ?? '';
  }

  get userName(): string {
    return this.userInfo?.name ?? '';
  }

  get userImageUrl(): string {
    return this.userInfo?.pictureUrl ?? 'assets/images/admin-icon.jpg';
  }

  set changePassRequired(value: boolean) {
    this.isChangePassRequired = value;
  }

  get changePassRequired(): boolean {
    return this.isChangePassRequired;
  }

  async refreshSession() {
    const sessionData = await Promise.resolve(
      lastValueFrom(
        this.http.get<DataResponse<UserInfo>>(
          urljoin(environment.backendServiceUrl, '/v1/account/me'),
        ),
      ),
    );

    this.userInfo = sessionData?.data;
  }

  isPhotoAlreadyInCart(photoId: string): boolean {
    return this.userInfo?.cart?.includes(photoId) ?? false;
  }
}
